/**
 * Upload one video from the browser straight into R2 (docs/VIDEO_UPLOADS.md
 * "Protocol"). The bytes never pass through our server:
 *
 *   1. read the clip's length and size locally (the server needs them up
 *      front for the quota and the limits; its encoder checks the truth later)
 *   2. POST /api/videos/uploads → a token and presigned URLs for the parts
 *   3. PUT each part to its URL — XHR, for progress events — a few at a time
 *   4. POST /:token/complete; the clip is then queued for the encoder
 *
 * A cancel DELETEs the upload, which frees the in-flight slot (2 per person)
 * and gives the minutes back. A failure after the upload exists (a part that
 * won't go, a lost connection) leaves it OPEN and says so (`resumable`): the
 * same file can carry on from the parts R2 already has (resumeVideo, V5),
 * in this tab or — re-picked — after the tab closed. The 48 h expiry and an
 * explicit cancel are what close it.
 *
 * Time left is estimated from what has been sent so far, so it says something
 * from the first part on (V1: tell people up front how long it will take).
 */

import VideoService from './videoService';
import UserImageService from './userImageService';
import { VIDEO_LIMITS } from '../config/videoLimits';

const PART_ATTEMPTS = 4;
const METADATA_TIMEOUT_MS = 15000;

const TYPE_BY_EXTENSION = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', qt: 'video/quicktime' };

/** The content type the server accepts for this file, or null. */
export function videoContentType(file) {
  const type = String(file?.type || '').toLowerCase();
  if (VIDEO_LIMITS.acceptedContentTypes.includes(type)) return type;
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  return TYPE_BY_EXTENSION[ext] || null;
}

/** Is this picked file a video (by type or extension)? */
export function isVideoFile(file) {
  return String(file?.type || '').startsWith('video/') || !!videoContentType(file);
}

/**
 * Length and size of a local video, read by the browser itself.
 * @returns {Promise<{ durationMs: number, width: number|null, height: number|null }|null>}
 */
export function readVideoInfo(file, { timeoutMs = METADATA_TIMEOUT_MS } = {}) {
  return new Promise((resolve) => {
    let url;
    let settled = false;
    const video = document.createElement('video');
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load?.();
      if (url) URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const seconds = Number(video.duration);
      if (!Number.isFinite(seconds) || seconds <= 0) return finish(null);
      finish({ durationMs: Math.round(seconds * 1000), width: video.videoWidth || null, height: video.videoHeight || null });
    };
    video.onerror = () => finish(null);
    try {
      url = URL.createObjectURL(file);
      video.src = url;
    } catch {
      finish(null);
    }
  });
}

/**
 * Why this file can't be uploaded, before anything is sent — the server's
 * limits (src/config/videoLimits.js), checked again server-side.
 * @returns {{ code: string, error: string }|null}
 */
export function checkVideoFile(file, info) {
  if (!videoContentType(file)) return { code: 'unsupported_type', error: 'Upload an MP4 or MOV video' };
  if (file.size > VIDEO_LIMITS.maxBytes) return { code: 'too_large', error: 'Videos can be up to 4 GB' };
  if (!info) return { code: 'unreadable', error: "This video couldn't be read" };
  if (info.durationMs > VIDEO_LIMITS.maxSeconds * 1000) {
    return { code: 'too_long', error: `Videos can be up to ${Math.round(VIDEO_LIMITS.maxSeconds / 60)} minutes` };
  }
  return null;
}

const cancelledError = () => Object.assign(new Error('cancelled'), { cancelled: true });

function putPart(url, blob, { onProgress, signal, createXhr }) {
  return new Promise((resolve, reject) => {
    // Already stopped: never open. (abort() on an opened, unsent XHR fires
    // no event, so the promise would never settle and stall the queue.)
    if (signal?.aborted) { reject(cancelledError()); return; }
    if (!url) { reject(Object.assign(new Error('no upload link for this part'), { status: 0 })); return; }
    const xhr = createXhr();
    const onAbort = () => { xhr.abort(); reject(cancelledError()); };
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`part upload failed (${xhr.status})`), { status: xhr.status }));
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('network error'), { status: 0 }));
    };
    xhr.onabort = () => reject(cancelledError());
    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(blob);
  });
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// The server says this upload is over: nothing left to resume.
const CLOSED_CODES = ['expired', 'upload_closed', 'not_found'];

/**
 * Upload a video. Never throws.
 *
 * @param {File} file
 * @param {object} context  trip_id, trip_day_id, activity_id, album_id, attraction_id, visibility
 * @param {object} [opts]
 * @param {(p: { phase: string, sentBytes: number, totalBytes: number, etaSeconds: number|null }) => void} [opts.onProgress]
 * @param {(video: object) => void} [opts.onCreated] - the upload exists (its token): from here a failure can be resumed
 * @param {AbortSignal} [opts.signal] - a cancel: the upload is deleted
 * @returns {Promise<{ success: boolean, video?: object, code?: string, error?: string, token?: string, resumable?: boolean }>}
 */
export async function uploadVideo(file, context = {}, opts = {}) {
  const { onProgress = () => {}, onCreated = null, signal, info = null, api = VideoService } = opts;
  const totalBytes = file.size;
  onProgress({ phase: 'preparing', sentBytes: 0, totalBytes, etaSeconds: null });
  const meta = info || await readVideoInfo(file);
  const refusal = checkVideoFile(file, meta);
  if (refusal) return { success: false, ...refusal };
  if (signal?.aborted) return { success: false, code: 'cancelled', error: 'Upload cancelled' };

  const created = await api.createUpload({
    duration_ms: meta.durationMs,
    size_bytes: totalBytes,
    content_type: videoContentType(file),
    ...(meta.width ? { width: meta.width } : {}),
    ...(meta.height ? { height: meta.height } : {}),
    ...context
  });
  if (!created.success) return { success: false, code: created.code || 'create_failed', error: created.error, quota: created.quota };
  onCreated?.(created.video);
  return sendRemaining(file, created.video, { firstUrls: created.parts, done: [] }, opts);
}

/**
 * Carry on an upload that's still open (VIDEO_PLAN V5): after a lost
 * connection, or after the tab closed — then with the file picked again.
 * Asks the server which parts R2 already has and sends only the rest.
 * Never throws.
 *
 * @param {File} file   - must be the same file (its size is checked against the declared one)
 * @param {string} token
 * @param {object} [opts] - as uploadVideo
 * @returns as uploadVideo, plus `already_complete` when it had finished
 *   meanwhile; code 'different_file' when the size doesn't match
 */
export async function resumeVideo(file, token, opts = {}) {
  const { onProgress = () => {}, signal, api = VideoService } = opts;
  onProgress({ phase: 'preparing', sentBytes: 0, totalBytes: file.size, etaSeconds: null });
  if (signal?.aborted) return { success: false, code: 'cancelled', error: 'Upload cancelled' };
  const status = await api.getUpload(token);
  if (!status.success) {
    return { success: false, code: status.code || 'resume_failed', error: status.error, token, resumable: !CLOSED_CODES.includes(status.code) };
  }
  const video = status.video;
  if (video.processing_status !== 'uploading') {
    // Finished meanwhile (a complete whose answer was lost), or closed.
    if (['queued', 'processing', 'ready'].includes(video.processing_status)) return { success: true, already_complete: true, video };
    return { success: false, code: video.processing_status === 'expired' ? 'expired' : 'upload_closed', token, resumable: false };
  }
  if (file.size !== video.declared_bytes) {
    return { success: false, code: 'different_file', error: 'That is not the file this upload started with', token, resumable: true };
  }
  return sendRemaining(file, video, { firstUrls: status.parts, done: status.uploaded_parts || [] }, opts);
}

/**
 * The parts R2 doesn't have yet, a few lanes at a time, then complete.
 * Shared by uploadVideo and resumeVideo.
 */
async function sendRemaining(file, video, { firstUrls = [], done = [] }, {
  onProgress = () => {},
  signal,
  concurrency = UserImageService.uploadConcurrency(),
  createXhr = () => new XMLHttpRequest(),
  api = VideoService,
  retryDelayMs = 1000
} = {}) {
  const { token, part_size: partSize, part_count: partCount } = video;
  const totalBytes = file.size;
  const partBytes = (n) => Math.max(0, Math.min(partSize, totalBytes - (n - 1) * partSize));

  const urls = new Map((firstUrls || []).map(p => [p.part_number, p.url]));
  const alreadyThere = new Set(done);
  // Parts R2 has confirmed (200 to the PUT). Upload progress events count a
  // part's bytes before its answer, so a failure reports only these.
  const confirmed = new Set(alreadyThere);
  const confirmedBytes = () => [...confirmed].reduce((sum, n) => sum + partBytes(n), 0);
  const loaded = new Map([...alreadyThere].map(n => [n, partBytes(n)])); // part → bytes sent so far
  const resumedBytes = [...loaded.values()].reduce((a, b) => a + b, 0);
  const started = Date.now();
  // One stop for every lane: the caller's cancel, or the first part that
  // fails for good. Without it the other lanes kept sending parts to an
  // upload already given up on, and their progress overwrote the failure.
  const stop = new AbortController();
  const onCallerAbort = () => stop.abort();
  if (signal?.aborted) stop.abort();
  else signal?.addEventListener('abort', onCallerAbort, { once: true });
  let settled = false;
  const report = (phase = 'uploading') => {
    if (settled) return;
    const sent = [...loaded.values()].reduce((a, b) => a + b, 0);
    const elapsed = (Date.now() - started) / 1000;
    // The rate counts this session's bytes only, not the parts R2 already had.
    const rate = elapsed > 1 && sent > resumedBytes ? (sent - resumedBytes) / elapsed : null;
    onProgress({ phase, sentBytes: sent, totalBytes, etaSeconds: rate ? Math.max(0, Math.round((totalBytes - sent) / rate)) : null });
  };

  // More URLs, signed from part `from` on (the server pages them, ≤100 at a time).
  const fetchUrls = async (from) => {
    const res = await api.getUpload(token, from);
    if (!res.success) throw Object.assign(new Error(res.error || 'could not refresh upload links'), { code: res.code });
    for (const p of res.parts || []) urls.set(p.part_number, p.url);
  };

  const sendPart = async (n) => {
    const start = (n - 1) * partSize;
    const blob = file.slice(start, Math.min(start + partSize, totalBytes));
    for (let attempt = 1; ; attempt++) {
      if (stop.signal.aborted) throw cancelledError();
      if (!urls.has(n)) await fetchUrls(n);
      try {
        await putPart(urls.get(n), blob, { onProgress: (b) => { loaded.set(n, b); report(); }, signal: stop.signal, createXhr });
        loaded.set(n, blob.size);
        confirmed.add(n);
        report();
        return;
      } catch (err) {
        if (err.cancelled || attempt >= PART_ATTEMPTS) throw err;
        loaded.set(n, 0);
        // An expired or refused URL: sign this part afresh.
        if (err.status === 403) urls.delete(n);
        await sleep(retryDelayMs * attempt);
      }
    }
  };

  const sendParts = async (numbers) => {
    let next = 0;
    const lane = async () => {
      try {
        while (next < numbers.length && !stop.signal.aborted) await sendPart(numbers[next++]);
      } catch (err) {
        stop.abort();
        throw err;
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, numbers.length)) }, lane));
  };

  try {
    report();
    await sendParts(Array.from({ length: partCount }, (_, i) => i + 1).filter(n => !alreadyThere.has(n)));
    // Stopped before any lane threw (a cancel during create): don't complete.
    if (stop.signal.aborted) throw cancelledError();
    report('finishing');
    let finished = await api.completeUpload(token);
    if (!finished.success && finished.code === 'parts_mismatch' && Array.isArray(finished.missing_parts) && finished.missing_parts.length) {
      // R2 lost or rejected some parts: send just those once more.
      for (const n of finished.missing_parts) urls.delete(n);
      await sendParts(finished.missing_parts);
      finished = await api.completeUpload(token);
    }
    if (!finished.success) throw Object.assign(new Error(finished.error || 'could not finish the upload'), { code: finished.code || 'complete_failed' });
    return { success: true, video: finished.video };
  } catch (err) {
    stop.abort();
    // A cancel is the caller's: free the slot and the minutes (best effort).
    if (signal?.aborted) {
      api.cancelUpload(token).catch?.(() => {});
      return { success: false, code: 'cancelled', error: 'Upload cancelled' };
    }
    // Anything else leaves the upload open, to resume from the parts R2 has —
    // unless the server says it's over.
    const code = err.cancelled ? 'upload_failed' : (err.code || 'upload_failed');
    return { success: false, code, error: err.message, token, resumable: !CLOSED_CODES.includes(code), sentBytes: confirmedBytes() };
  } finally {
    settled = true;
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

/** "about 2 min left" style rounding, in seconds → { unit, count } for i18n. */
export function roundEta(seconds) {
  if (seconds == null) return null;
  if (seconds < 60) return { unit: 'seconds', count: Math.max(5, Math.ceil(seconds / 5) * 5) };
  if (seconds < 3600) return { unit: 'minutes', count: Math.ceil(seconds / 60) };
  return { unit: 'hours', count: Math.round((seconds / 3600) * 10) / 10 };
}
